export default class NineSlicedContainer extends Phaser.GameObjects.Container {
    private _baseTexture: Phaser.Textures.TextureSource;
    private _baseKey: string;
    private _top: number;
    private _right: number;
    private _bottom: number;
    private _left: number;
    private _height: number;
    private _width: number;
    private _originX: number;
    private _originY: number;
    interactive = false;

    constructor(
        scene: Phaser.Scene,
        key: string,
        width: number,
        height: number,
        top: number,
        right: number,
        bottom: number,
        left: number,
        originX = 0.5,
        originY = 0.5,
    ) {
        super(scene);

        this._baseKey = key;
        this._top = top;
        this._right = right;
        this._bottom = bottom;
        this._left = left;
        this._originX = originX;
        this._originY = originY;

        this.resize(width, height);
    }

    get slicedHeight(): number {
        return this._height;
    }

    set slicedHeight(value: number) {
        this.resize(this._width, value);
    }

    get slicedWidth(): number {
        return this._width;
    }

    set slicedWidth(value: number) {
        this.resize(value, this._height);
    }

    resize(width: number, height: number): void {
        if (this.scene == null) return;

        for (const obj of [...this.list]) obj.destroy();

        if (isNaN(width)) width = 1;
        if (isNaN(height)) height = 1;
        width = Math.max(width, 1);
        height = Math.max(height, 1);

        this.setSize(width, height);
        this._width = width;
        this._height = height;

        if (this._width < this._right + this._left) {
            this._width = this._right + this._left; //kludge: if our slices are greater than our width just force our width to match to avoid rendering issues
        }
        if (this._height < this._top + this._bottom) {
            this._height = this._top + this._bottom; //kludge: if our slices are greater than our height just force our height to match to avoid rendering issues
        }

        this._baseTexture = this.scene.textures.get(this._baseKey).source[0]; //todo: could their be multiple?

        //top left
        this.renderPiece(0, 0, new Phaser.Geom.Rectangle(0, 0, this._left, this._top));

        //top right
        this.renderPiece(
            width - this._right,
            0,
            new Phaser.Geom.Rectangle(this._baseTexture.width - this._right, 0, this._right, this._top)
        );

        //top middle
        this.renderStretchPiece(
            this._left,
            0,
            new Phaser.Geom.Rectangle(this._left, 0, this._baseTexture.width - this._left - this._right, this._top),
            width - this._left - this._right,
            this._top
        );

        //bottom left
        this.renderPiece(
            0,
            height - this._bottom,
            new Phaser.Geom.Rectangle(0, this._baseTexture.height - this._bottom, this._left, this._bottom)
        );

        //bottom right
        this.renderPiece(
            width - this._right,
            height - this._bottom,
            new Phaser.Geom.Rectangle(
                this._baseTexture.width - this._right,
                this._baseTexture.height - this._bottom,
                this._right,
                this._bottom
            )
        );

        //bottom middle
        this.renderStretchPiece(
            this._left,
            height - this._bottom,
            new Phaser.Geom.Rectangle(
                this._left,
                this._baseTexture.height - this._bottom,
                this._baseTexture.width - this._left - this._right,
                this._bottom
            ),
            width - this._left - this._right,
            this._bottom
        );

        //left middle
        this.renderStretchPiece(
            0,
            this._top,
            new Phaser.Geom.Rectangle(0, this._top, this._left, this._baseTexture.height - this._top - this._bottom),
            this._left,
            height - this._top - this._bottom
        );

        //right middle
        this.renderStretchPiece(
            width - this._right,
            this._top,
            new Phaser.Geom.Rectangle(
                this._baseTexture.width - this._right,
                this._top,
                this._right,
                this._baseTexture.height - this._top - this._bottom
            ),
            this._right,
            height - this._top - this._bottom
        );

        //middle
        this.renderStretchPiece(
            this._left,
            this._top,
            new Phaser.Geom.Rectangle(
                this._left,
                this._top,
                this._baseTexture.width - this._left - this._right,
                this._baseTexture.height - this._top - this._bottom
            ),
            width - this._left - this._right,
            height - this._top - this._bottom
        );

        if (this.interactive) this.setInteractive();

    }

    renderPiece(x: number, y: number, frame: Phaser.Geom.Rectangle): void {
        const sprite = new Phaser.GameObjects.Image(this.scene, 0, 0, this._baseKey);
        this.add(sprite);
        sprite.frame = new Phaser.Textures.Frame(sprite.texture, "", 0, frame.x, frame.y, frame.width, frame.height);
        sprite.setSizeToFrame(sprite.frame);
        sprite.setDisplaySize(sprite.width, sprite.height);
        sprite.setOrigin(0, 0);
        sprite.setPosition(x - this.width * this._originX, y - this.height * this._originY);
    }

    renderStretchPiece(x: number, y: number, frame: Phaser.Geom.Rectangle, width: number, height: number): void {
        const sprite = new Phaser.GameObjects.Image(this.scene, 0, 0, this._baseKey);
        this.add(sprite);
        sprite.frame = new Phaser.Textures.Frame(sprite.texture, "", 0, frame.x, frame.y, frame.width, frame.height);
        sprite.setSizeToFrame(sprite.frame);
        sprite.setDisplaySize(width, height);
        sprite.setOrigin(0, 0);
        sprite.setPosition(x - this.width * this._originX, y - this.height * this._originY);
    }

    setInteractive(_hitArea?: unknown, _callback?: unknown, _dropZone?: boolean | undefined): this {
        for (const obj of this.list) {
            obj.setInteractive();
            obj.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, (ptr: Phaser.Input.Pointer) => this.emit(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, ptr));
        }
        this.interactive = true;
        return this;
    }

    destroy(): void {
        super.destroy();
    }
}
